import { FunctionComponent } from 'react';
import { SimpleObject } from '../../types';
import '../../css/PrivateEstudioConexion.css';

export const UMBRAL_IMPACTO_PP_DEFECTO = 2;

// ═══════════════════════════════════════════════════════════════════════════
// NORM-018 A (§3.1.3 schema 1.1.0) — contrato `bandas_tension` compartido
// Fuente de verdad: el artefacto del motor (resuelto según densidad del
// estudio); el frontend solo dibuja. Cuando el artefacto es schema 1.0 o
// falta el bloque, caemos al corredor absoluto normativo fijo y sin
// bandas relativas por nivel (vista combinada).
// ═══════════════════════════════════════════════════════════════════════════

export type EcgNivelTension = 'todos' | 'mt' | 'bt';

export type EcgBandaPu = { min: number; max: number };

export type EcgBandasTension = {
  /** `"alta" | "media" | "baja" | "muy_baja"` cuando viene; `null` si no se resolvió. */
  densidad: string | null;
  /** Origen reportado por motor: `"bd"` o `"fallback_default"`. */
  densidadFuente: string | null;
  absolutoPu: EcgBandaPu;
  mtPu: EcgBandaPu | null;
  btPu: EcgBandaPu | null;
  deltaVPcMaxPct: number | null;
  origenNormativo: string | null;
  /** Si venimos de un artefacto schema 1.0 o no hay bloque, solo usamos absoluto. */
  schemaLegacy: boolean;
};

/** Corredor absoluto normativo por defecto (NORM-007/008) si el artefacto no lo trae. */
export const ECG_CORREDOR_ABSOLUTO_DEFAULT: EcgBandaPu = { min: 0.85, max: 1.1 };

const ECG_COLOR_ABSOLUTO = '#b91c1c';
const ECG_COLOR_MT = '#e06c00';
const ECG_COLOR_BT = '#0072b2';

export const ECG_BANDAS_COLORS = {
  absoluto: ECG_COLOR_ABSOLUTO,
  mt: ECG_COLOR_MT,
  bt: ECG_COLOR_BT
};

function parseBandaPu(x: unknown): EcgBandaPu | null {
  if (!x || typeof x !== 'object') return null;
  const o = x as SimpleObject;
  const min = Number(o.min ?? o.v_min_pu ?? o.vMinPu);
  const max = Number(o.max ?? o.v_max_pu ?? o.vMaxPu);
  if (!Number.isFinite(min) || !Number.isFinite(max) || max <= min) return null;
  return { min, max };
}

/**
 * Parsea `bandas_tension` desde la raíz del artefacto (§3.1.3 II). Si no está
 * presente (schema 1.0) retorna un objeto con solo el corredor absoluto por
 * defecto y `schemaLegacy: true` — la UI debe mostrar vista combinada sin
 * filtro MT/BT.
 */
export function parseBandasTensionArtefacto(raw: unknown): EcgBandasTension {
  if (raw && typeof raw === 'object') {
    const o = raw as SimpleObject;
    const abs = parseBandaPu(o.absoluto_pu ?? o.absolutoPu);
    const mt = parseBandaPu(o.mt_pu ?? o.mtPu);
    const bt = parseBandaPu(o.bt_pu ?? o.btPu);
    const deltaMax = Number(o.delta_v_pc_max_pct ?? o.deltaVPcMaxPct);
    if (abs) {
      return {
        densidad: (o.densidad as string) ?? null,
        densidadFuente: (o.densidad_fuente as string) ?? (o.densidadFuente as string) ?? null,
        absolutoPu: abs,
        mtPu: mt,
        btPu: bt,
        deltaVPcMaxPct: Number.isFinite(deltaMax) ? deltaMax : null,
        origenNormativo: (o.origen_normativo as string) ?? (o.origenNormativo as string) ?? null,
        schemaLegacy: false
      };
    }
  }
  return {
    densidad: null,
    densidadFuente: null,
    absolutoPu: { ...ECG_CORREDOR_ABSOLUTO_DEFAULT },
    mtPu: null,
    btPu: null,
    deltaVPcMaxPct: null,
    origenNormativo: null,
    schemaLegacy: true
  };
}

/**
 * Extrae `bandas_tension` del payload del artefacto (Fase 1 o Fase 2). El
 * backend TS entrega `resultadoJson` crudo desde BD (camelizado en el wrapper
 * de consulta), por lo que admitimos snake o camel.
 */
export function extractBandasTensionArtefacto(data: SimpleObject | null | undefined): EcgBandasTension {
  if (!data || typeof data !== 'object') {
    return parseBandasTensionArtefacto(null);
  }
  const blob = (data as SimpleObject).bandas_tension ?? (data as SimpleObject).bandasTension;
  return parseBandasTensionArtefacto(blob);
}

/** ARTIFACT_SCHEMA_VERSION — "1.1.0" incluye `bandas_tension` + deciles por nivel + boxplot por nivel. */
export function artifactSchemaVersion(data: SimpleObject | null | undefined): string | null {
  if (!data || typeof data !== 'object') return null;
  const v = (data as SimpleObject).artifact_schema_version ?? (data as SimpleObject).artifactSchemaVersion;
  return typeof v === 'string' ? v : null;
}

export function isSchemaAlMenos11(data: SimpleObject | null | undefined): boolean {
  const v = artifactSchemaVersion(data);
  if (!v) return false;
  const parts = v.split('.').map((x) => Number(x));
  if (parts.length < 2 || parts.some((n) => !Number.isFinite(n))) return false;
  return parts[0] > 1 || (parts[0] === 1 && parts[1] >= 1);
}

type EscenarioOption = { value: string; label: string };

type ResultadoTecnicoParsed = {
  estudiosDiarios: SimpleObject[];
  estudiosDiariosBase: SimpleObject[];
  estudiosSnapshots: SimpleObject[];
  estudiosSnapshotsBase: SimpleObject[];
  cortocircuito: SimpleObject | null;
  impactoProyecto: SimpleObject | null;
  cumpleDiarios: boolean | undefined;
  cumpleSnapshots: boolean | undefined;
  cumpleCortocircuito: boolean | undefined;
  decisionGeneral: string;
  restricciones: string[];
};

function arrOrEmpty(data: SimpleObject, snake: string, camel?: string): SimpleObject[] {
  const v = camel ? (data[snake] ?? data[camel]) : data[snake];
  return Array.isArray(v) ? (v as SimpleObject[]) : [];
}

function asStringArray(data: SimpleObject, snake: string, camel?: string): string[] {
  const v = camel ? (data[snake] ?? data[camel]) : data[snake];
  if (!Array.isArray(v)) return [];
  return (v as unknown[]).map((x) => String(x));
}

export function parseResultadoTecnico(data: SimpleObject): ResultadoTecnicoParsed {
  return {
    estudiosDiarios: arrOrEmpty(data, 'estudios_diarios', 'estudiosDiarios'),
    estudiosDiariosBase: arrOrEmpty(data, 'estudios_diarios_base', 'estudiosDiariosBase'),
    estudiosSnapshots: arrOrEmpty(data, 'estudios_snapshots', 'estudiosSnapshots'),
    estudiosSnapshotsBase: arrOrEmpty(data, 'estudios_snapshots_base', 'estudiosSnapshotsBase'),
    cortocircuito: (data.cortocircuito as SimpleObject) ?? null,
    impactoProyecto: (data.impacto_proyecto as SimpleObject) ?? (data.impactoProyecto as SimpleObject) ?? null,
    cumpleDiarios: data.cumple_diarios as boolean | undefined,
    cumpleSnapshots: data.cumple_snapshots as boolean | undefined,
    cumpleCortocircuito: data.cumple_cortocircuito as boolean | undefined,
    decisionGeneral: String(data.decision_general ?? data.decisionGeneral ?? ''),
    restricciones: asStringArray(data, 'restricciones_incumplidas', 'restriccionesIncumplidas')
  };
}

export function umbralPctFromResultado(data: SimpleObject): number {
  const ip = (data.impacto_proyecto as SimpleObject) ?? (data.impactoProyecto as SimpleObject);
  const u = ip?.umbral_impacto_pct ?? ip?.umbralImpactoPct;
  const n = Number(u);
  return Number.isFinite(n) ? n : UMBRAL_IMPACTO_PP_DEFECTO;
}

function mapPorEscenario(rows: SimpleObject[]): Map<string, SimpleObject> {
  const m = new Map<string, SimpleObject>();
  for (const r of rows) {
    const esc = String(r.escenario ?? '');
    if (esc) m.set(esc, r);
  }
  return m;
}

function formatoEscenarioAmigable(esc: string): string {
  if (!esc) return '—';
  return esc.replace(/_/g, ' ');
}

/**
 * Etiquetas cortas para snapshots en selectores (detalle API usa `__sin_proyecto`;
 * resumen JSON usa clave sin sufijo).
 *
 * NORM-002 / PLAN_NORM_002_SNAPSHOTS_DEMANDA_ANUAL §4.7–§8.5: el motor ahora
 * emite 4 snapshots por tipo de proyecto con sufijo `_anual` (sin duplicación
 * verano/invierno). Se mantienen las claves `_verano` / `_invierno` como
 * alias durante la transición para no romper vistas de estudios históricos;
 * pueden retirarse cuando se reejecuten esos estudios contra el nuevo motor.
 */
export const ETIQUETAS_SNAPSHOT_CUATRO: Record<string, string> = {
  snapshot_dmin_sinGD_anual: 'Snapshot Dmin (anual)',
  snapshot_dmax_sinGD_anual: 'Snapshot Dmax (anual)',
  snapshot_dmin_genmax_anual: 'Snapshot Dmin gen. máx (anual)',
  snapshot_dmax_genmax_anual: 'Snapshot Dmax gen. máx (anual)',
  snapshot_dmin_sinGD_verano: 'Snapshot Dmin — verano (estudio anterior)',
  snapshot_dmin_sinGD_invierno: 'Snapshot Dmin — invierno (estudio anterior)',
  snapshot_dmax_sinGD_verano: 'Snapshot Dmax — verano (estudio anterior)',
  snapshot_dmax_sinGD_invierno: 'Snapshot Dmax — invierno (estudio anterior)',
  snapshot_dmin_genmax_verano: 'Snapshot Dmin — verano (estudio anterior)',
  snapshot_dmin_genmax_invierno: 'Snapshot Dmin — invierno (estudio anterior)',
  snapshot_dmax_genmax_verano: 'Snapshot Dmax — verano (estudio anterior)',
  snapshot_dmax_genmax_invierno: 'Snapshot Dmax — invierno (estudio anterior)'
};

function normalizarClaveEscenarioSnapshot(esc: string): string {
  return esc.replace(/__con_proyecto$/i, '').replace(/__sin_proyecto$/i, '');
}

function etiquetaSnapshotCuatroCasos(esc: string): string {
  const key = normalizarClaveEscenarioSnapshot(esc);
  return ETIQUETAS_SNAPSHOT_CUATRO[key] ?? formatoEscenarioAmigable(esc);
}

export function escenariosDiariosConProyecto(rt: ResultadoTecnicoParsed): EscenarioOption[] {
  return rt.estudiosDiarios.map((r) => ({
    value: String(r.escenario ?? ''),
    label: formatoEscenarioAmigable(String(r.escenario ?? ''))
  }));
}

export function escenariosSnapshotConProyecto(rt: ResultadoTecnicoParsed): EscenarioOption[] {
  return rt.estudiosSnapshots
    .filter((r) => String(r.escenario ?? '').includes('genmax'))
    .map((r) => ({
      value: String(r.escenario ?? ''),
      label: etiquetaSnapshotCuatroCasos(String(r.escenario ?? ''))
    }));
}

function escenarioSnapshotBase(escConProyecto: string): string | null {
  if (!escConProyecto.includes('genmax')) return null;
  return escConProyecto.replace('genmax', 'sinGD');
}

function etiquetaDecisionGeneral(codigo: string): string {
  if (codigo === 'cumple') return 'Cumple criterios técnicos agregados';
  if (codigo === 'no_cumple') return 'No cumple (restricción crítica o impacto no admitido)';
  if (codigo === 'requiere_obras') return 'Requiere obras o ajustes en la red';
  return codigo || '—';
}

function numO(v: unknown): number | null {
  if (v === null || v === undefined) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function fmtPct(n: number | null): string {
  if (n === null) return '—';
  return `${n.toFixed(2)}%`;
}

type FilaComparativa = {
  criterio: string;
  sinProyecto: string;
  conProyecto: string;
  delta: string;
  cumple: boolean | null;
};

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

/** Verde si Δ ≤ umbral pp (empeoramiento); mejoras (Δ negativo) siempre cumplen. */
function deltaCumpleUmbralImpacto(delta: number | null, umbral: number): boolean | null {
  if (delta === null) return null;
  return !(delta > umbral);
}

function semaforosDiarioDeltaVsBase(
  con: SimpleObject | undefined,
  base: SimpleObject | undefined,
  umbral: number
): { tension: boolean | null; corrientes: boolean | null; trafos: boolean | null; perdidas: boolean | null } {
  if (!con) {
    return { tension: null, corrientes: null, trafos: null, perdidas: null };
  }
  const bT = base ? numO(base.pct_buses_con_problema) : null;
  const cT = numO(con.pct_buses_con_problema);
  const bL = base ? numO(base.pct_lineas_con_problema) : null;
  const cL = numO(con.pct_lineas_con_problema);
  const bTr = base ? numO(base.pct_trafos_con_problema) : null;
  const cTr = numO(con.pct_trafos_con_problema);
  const bP = base ? numO(base.pct_tiempo_perdidas_sobre_umbral) : null;
  const cP = numO(con.pct_tiempo_perdidas_sobre_umbral);
  const dT = bT !== null && cT !== null ? round2(cT - bT) : null;
  const dL = bL !== null && cL !== null ? round2(cL - bL) : null;
  const dTr = bTr !== null && cTr !== null ? round2(cTr - bTr) : null;
  const dP = bP !== null && cP !== null ? round2(cP - bP) : null;
  return {
    tension: deltaCumpleUmbralImpacto(dT, umbral),
    corrientes: deltaCumpleUmbralImpacto(dL, umbral),
    trafos: deltaCumpleUmbralImpacto(dTr, umbral),
    perdidas: deltaCumpleUmbralImpacto(dP, umbral)
  };
}

function semaforosSnapshotDeltaVsBase(
  con: SimpleObject | undefined,
  base: SimpleObject | undefined,
  umbral: number
): { tension: boolean | null; corrientes: boolean | null; trafos: boolean | null } {
  if (!con) {
    return { tension: null, corrientes: null, trafos: null };
  }
  const bT = base ? numO(base.pct_buses_fuera_banda) : null;
  const cT = numO(con.pct_buses_fuera_banda);
  const bL = base ? numO(base.pct_lineas_sobrecarga) : null;
  const cL = numO(con.pct_lineas_sobrecarga);
  const bTr = base ? numO(base.pct_trafos_sobrecarga) : null;
  const cTr = numO(con.pct_trafos_sobrecarga);
  const dT = bT !== null && cT !== null ? round2(cT - bT) : null;
  const dL = bL !== null && cL !== null ? round2(cL - bL) : null;
  const dTr = bTr !== null && cTr !== null ? round2(cTr - bTr) : null;
  return {
    tension: deltaCumpleUmbralImpacto(dT, umbral),
    corrientes: deltaCumpleUmbralImpacto(dL, umbral),
    trafos: deltaCumpleUmbralImpacto(dTr, umbral)
  };
}

function derivarCasoBaseIncumpleNormativa(rt: ResultadoTecnicoParsed): boolean | null {
  let found = false;
  for (const r of rt.estudiosDiariosBase) {
    const hasMark = [r.cumple_tension, r.cumple_corrientes, r.cumple_trafos, r.cumple_perdidas].some(
      (x) => typeof x === 'boolean'
    );
    if (!hasMark) continue;
    found = true;
    if (
      r.cumple_tension === false ||
      r.cumple_corrientes === false ||
      r.cumple_trafos === false ||
      r.cumple_perdidas === false
    ) {
      return true;
    }
  }
  for (const r of rt.estudiosSnapshotsBase) {
    const hasMark = [r.cumple_tension, r.cumple_corrientes, r.cumple_trafos].some((x) => typeof x === 'boolean');
    if (!hasMark) continue;
    found = true;
    if (r.cumple_tension === false || r.cumple_corrientes === false || r.cumple_trafos === false) {
      return true;
    }
  }
  return found ? false : null;
}

function casoBaseIncumpleNormativaDesdeResultado(data: SimpleObject, rt: ResultadoTecnicoParsed): boolean | null {
  const top = data.caso_base_incumple_normativa ?? data.casoBaseIncumpleNormativa;
  if (typeof top === 'boolean') return top;
  const ip = rt.impactoProyecto;
  const nested = ip?.caso_base_incumple_normativa ?? ip?.casoBaseIncumpleNormativa;
  if (typeof nested === 'boolean') return nested;
  return derivarCasoBaseIncumpleNormativa(rt);
}

/** Si existe impacto_proyecto.impacto_en_diarios, coincide con el motor; si no, se deriva de Δ vs base y umbral. */
function cumpleCriterioImpactoDiarios(rt: ResultadoTecnicoParsed, umbral: number): boolean | null {
  const ip = rt.impactoProyecto;
  const impactoEnDiarios = ip?.impacto_en_diarios ?? ip?.impactoEnDiarios;
  if (typeof impactoEnDiarios === 'boolean') return !impactoEnDiarios;
  if (rt.estudiosDiarios.length === 0) return null;
  const mapB = mapPorEscenario(rt.estudiosDiariosBase);
  let anyNull = false;
  for (const r of rt.estudiosDiarios) {
    const baseRow = mapB.get(String(r.escenario ?? ''));
    const sm = semaforosDiarioDeltaVsBase(r, baseRow, umbral);
    for (const v of [sm.tension, sm.corrientes, sm.trafos, sm.perdidas]) {
      if (v === false) return false;
      if (v === null) anyNull = true;
    }
  }
  return anyNull ? null : true;
}

function cumpleCriterioImpactoSnapshots(rt: ResultadoTecnicoParsed, umbral: number): boolean | null {
  const ip = rt.impactoProyecto;
  const impactoEnSnaps = ip?.impacto_en_snapshots ?? ip?.impactoEnSnapshots;
  if (typeof impactoEnSnaps === 'boolean') return !impactoEnSnaps;
  const genmax = rt.estudiosSnapshots.filter((r) => String(r.escenario ?? '').includes('genmax'));
  if (genmax.length === 0) return null;
  const mapB = mapPorEscenario(rt.estudiosSnapshotsBase);
  let anyNull = false;
  for (const r of genmax) {
    const esc = String(r.escenario ?? '');
    const escB = escenarioSnapshotBase(esc);
    const baseRow = escB ? mapB.get(escB) : undefined;
    const sm = semaforosSnapshotDeltaVsBase(r, baseRow, umbral);
    for (const v of [sm.tension, sm.corrientes, sm.trafos]) {
      if (v === false) return false;
      if (v === null) anyNull = true;
    }
  }
  return anyNull ? null : true;
}

function textBooleanTriState(v: boolean | null): string {
  if (v === true) return 'Sí';
  if (v === false) return 'No';
  return '—';
}

type ImpactoTriState = 'cumple' | 'warning' | 'no-cumple' | 'nd';

/** Lista de trafos en JSON motor Julia: clave `transformadores` (ver `generar_refuerzos_json` en resultados.jl). */
export function listaTrafosRefuerzosJson(j: SimpleObject): SimpleObject[] {
  const t = j.trafos ?? j.transformadores;
  return Array.isArray(t) ? (t as SimpleObject[]) : [];
}

function hayRefuerzosSugeridos(refuerzosJson: SimpleObject | null | undefined): boolean {
  if (!refuerzosJson || typeof refuerzosJson !== 'object') return false;
  const nL = numO(refuerzosJson.n_lineas ?? refuerzosJson.nLineas) ?? 0;
  const nT = numO(refuerzosJson.n_trafos ?? refuerzosJson.nTrafos) ?? 0;
  if (nL + nT > 0) return true;
  const arrL = refuerzosJson.lineas;
  if (Array.isArray(arrL) && arrL.length > 0) return true;
  if (listaTrafosRefuerzosJson(refuerzosJson).length > 0) return true;
  return false;
}

function impactoTriState(
  cumpleImpacto: boolean | null,
  tieneRefuerzos: boolean
): ImpactoTriState {
  if (cumpleImpacto === null) return 'nd';
  if (!cumpleImpacto) return 'no-cumple';
  return tieneRefuerzos ? 'warning' : 'cumple';
}

function etiquetaImpactoTriState(ts: ImpactoTriState): string {
  if (ts === 'cumple') return 'Sin impacto atribuible';
  if (ts === 'warning') return 'Sin impacto detectado, pero se identificaron refuerzos en la red';
  if (ts === 'no-cumple') return 'Impacto atribuible al proyecto';
  return '—';
}

function restriccionesAtribuiblesDesdeImpacto(ip: SimpleObject | null | undefined, umbral: number): string[] {
  if (!ip) return [];
  const claves: [string, string][] = [
    ['tension', 'Tensión'],
    ['corrientes', 'Líneas / corrientes'],
    ['trafos', 'Transformadores'],
    ['perdidas', 'Pérdidas']
  ];
  const out: string[] = [];
  const bloques = [
    ...(Array.isArray(ip.analisis_diarios) ? (ip.analisis_diarios as SimpleObject[]) : []),
    ...(Array.isArray(ip.analisis_snapshots) ? (ip.analisis_snapshots as SimpleObject[]) : [])
  ];
  const excedidas = new Set<string>();
  for (const analisis of bloques) {
    for (const [key, label] of claves) {
      if (excedidas.has(key)) continue;
      const bloque = analisis[key] as SimpleObject | undefined;
      if (!bloque || typeof bloque !== 'object') continue;
      const delta = numO(bloque.delta_pct);
      if (delta !== null && delta > umbral) {
        excedidas.add(key);
        out.push(label);
      }
    }
  }
  return out;
}

function analisisDiarioPorEscenario(rt: ResultadoTecnicoParsed, esc: string): SimpleObject | undefined {
  const lista = rt.impactoProyecto?.analisis_diarios ?? rt.impactoProyecto?.analisisDiarios;
  if (!Array.isArray(lista)) return undefined;
  return lista.find((x) => (x as SimpleObject).escenario === esc) as SimpleObject | undefined;
}

function analisisSnapshotPorEscenario(rt: ResultadoTecnicoParsed, esc: string): SimpleObject | undefined {
  const lista = rt.impactoProyecto?.analisis_snapshots ?? rt.impactoProyecto?.analisisSnapshots;
  if (!Array.isArray(lista)) return undefined;
  return lista.find((x) => (x as SimpleObject).escenario === esc) as SimpleObject | undefined;
}

/**
 * NORM-018 A — Veredicto por criterio (N_base/N_proj + ΔV PC) leído de
 * `impacto_proyecto.analisis_<tipo>` del motor, sin regla Δ>2 pp.
 *
 * Fase 1 lo aplica por escenario seleccionado; Fase 2 lo usa para el
 * "Con proyecto (sin mitigación)" (desde `resultadoTecnicoData.impacto_proyecto`)
 * y para cada medida (desde `mitItem.analisis_<tipo>`).
 *
 * Reglas:
 * - Snapshots: `tiene_impacto = OR(<criterio>.tiene_impacto en cualquier escenario)`
 * - Diarios: idem sobre `analisis_diarios`.
 * - `null` si no hay entradas.
 */
export function tieneImpactoPorCriterioEnLista(
  lista: SimpleObject[] | null | undefined,
  criterio: 'tension' | 'corrientes' | 'trafos'
): boolean | null {
  if (!Array.isArray(lista) || lista.length === 0) return null;
  let sawAny = false;
  let anyTrue = false;
  for (const a of lista) {
    const sub = (a as SimpleObject)?.[criterio] as SimpleObject | undefined;
    if (!sub || typeof sub !== 'object') continue;
    const ti = sub.tiene_impacto ?? sub.tieneImpacto;
    if (typeof ti === 'boolean') {
      sawAny = true;
      if (ti) { anyTrue = true; break; }
    }
  }
  return sawAny ? anyTrue : null;
}

/** Listas de análisis por tipo — cubre snake y camelCase del wrapper de BD. */
export function analisisSnapshotsLista(src: SimpleObject | null | undefined): SimpleObject[] | null {
  if (!src || typeof src !== 'object') return null;
  const raw = (src as SimpleObject).analisis_snapshots ?? (src as SimpleObject).analisisSnapshots;
  return Array.isArray(raw) ? (raw as SimpleObject[]) : null;
}

export function analisisDiariosLista(src: SimpleObject | null | undefined): SimpleObject[] | null {
  if (!src || typeof src !== 'object') return null;
  const raw = (src as SimpleObject).analisis_diarios ?? (src as SimpleObject).analisisDiarios;
  return Array.isArray(raw) ? (raw as SimpleObject[]) : null;
}

/** Entry de análisis (snapshot o diario) para un escenario específico. */
export function analisisPorEscenario(
  lista: SimpleObject[] | null | undefined,
  escenario: string
): SimpleObject | undefined {
  if (!Array.isArray(lista) || !escenario) return undefined;
  return lista.find((x) => String((x as SimpleObject).escenario ?? '') === escenario) as SimpleObject | undefined;
}

/** Exportado para que Fase 2 use mismo coloreado que Fase 1 en Δ pp con signo. */
export function celdaDeltaSignoClassExport(delta: string): string {
  return celdaDeltaSignoClass(delta);
}

const SemaforoNullable: FunctionComponent<{ ok: boolean | null; title?: string }> = ({ ok, title }) => {
  if (ok === null) {
    return (
      <span
        className='ecg-semaforo-icon nd'
        style={{ display: 'inline-block', verticalAlign: 'middle' }}
        title={title ?? 'Sin datos de caso base para comparar'}
      />
    );
  }
  return (
    <span
      className={`ecg-semaforo-icon ${ok ? 'cumple' : 'no-cumple'}`}
      style={{ display: 'inline-block', verticalAlign: 'middle' }}
      title={title}
    />
  );
};

export const BloqueResumenImpactoTecnico: FunctionComponent<{
  data: SimpleObject;
  refuerzosJson?: SimpleObject | null;
  /** En `/mitigaciones`: solo resumen agregado + aviso de refuerzos (si aplica); sin tablas por escenario ni texto normativo largo. */
  mostrarDetalleEscenarios?: boolean;
}> = ({ data, refuerzosJson, mostrarDetalleEscenarios = true }) => {
  const rt = parseResultadoTecnico(data);
  const umbral = umbralPctFromResultado(data);
  const cc = rt.cortocircuito;
  const ip = rt.impactoProyecto;
  const cumpleDiariosImpacto = cumpleCriterioImpactoDiarios(rt, umbral);
  const cumpleSnapshotsImpacto = cumpleCriterioImpactoSnapshots(rt, umbral);
  const tieneRefuerzos = hayRefuerzosSugeridos(refuerzosJson);
  const cumpleGlobalImpacto =
    cumpleDiariosImpacto === null && cumpleSnapshotsImpacto === null
      ? null
      : (cumpleDiariosImpacto !== false && cumpleSnapshotsImpacto !== false);
  const triState = impactoTriState(cumpleGlobalImpacto, tieneRefuerzos);

  const casoBaseMalo = mostrarDetalleEscenarios
    ? casoBaseIncumpleNormativaDesdeResultado(data, rt)
    : null;
  const mapDiarioBase = mostrarDetalleEscenarios ? mapPorEscenario(rt.estudiosDiariosBase) : new Map();
  const mapSnapBase = mostrarDetalleEscenarios ? mapPorEscenario(rt.estudiosSnapshotsBase) : new Map();
  const restriccionesImpacto = mostrarDetalleEscenarios
    ? restriccionesAtribuiblesDesdeImpacto(ip, umbral)
    : [];

  return (
    <div className='ecg-tecnico-resumen'>
      <div className='ecg-form-row' style={{ marginBottom: '1rem' }}>
        <div className='ecg-info-item'>
          <span className='ecg-info-label'>Decisión general</span>
          <span className='ecg-info-value'>{etiquetaDecisionGeneral(rt.decisionGeneral)}</span>
        </div>
        <div className='ecg-info-item'>
          <span className='ecg-info-label'>Flujo diario (adicional) — variación respecto al caso base</span>
          <span className='ecg-info-value'>
            <span
              className={`ecg-semaforo-icon ${
                cumpleDiariosImpacto === true ? 'cumple' : cumpleDiariosImpacto === false ? 'no-cumple' : 'nd'
              }`}
              style={{ display: 'inline-block', verticalAlign: 'middle', marginRight: 6 }}
            />
            {textBooleanTriState(cumpleDiariosImpacto)}
          </span>
        </div>
        <div className='ecg-info-item'>
          <span className='ecg-info-label'>Snapshots — variación respecto al caso base</span>
          <span className='ecg-info-value'>
            <span
              className={`ecg-semaforo-icon ${
                cumpleSnapshotsImpacto === true
                  ? 'cumple'
                  : cumpleSnapshotsImpacto === false
                    ? 'no-cumple'
                    : 'nd'
              }`}
              style={{ display: 'inline-block', verticalAlign: 'middle', marginRight: 6 }}
            />
            {textBooleanTriState(cumpleSnapshotsImpacto)}
          </span>
        </div>
        <div className='ecg-info-item'>
          <span className='ecg-info-label'>Cortocircuito (Icc / ruptura)</span>
          <span className='ecg-info-value'>
            {cc == null ? (
              '—'
            ) : (
              <>
                <span
                  className={`ecg-semaforo-icon ${cc.cumple === true ? 'cumple' : 'no-cumple'}`}
                  style={{ display: 'inline-block', verticalAlign: 'middle', marginRight: 6 }}
                />
                {cc.cumple === true ? 'Cumple' : 'No cumple'}
                {numO(cc.pct_sobre_umbral) !== null ? ` — ${fmtPct(numO(cc.pct_sobre_umbral))} elementos sobre umbral` : ''}
              </>
            )}
          </span>
        </div>
      </div>

      <div className='ecg-subsection' style={{ marginBottom: '1rem' }}>
        <p className='ecg-subsection-title'>Indicador de variación respecto al caso base (histórico)</p>
        <div className='ecg-form-row'>
          <div className='ecg-info-item'>
            <span className='ecg-info-label'>Veredicto de variación del producto</span>
            <span className='ecg-info-value'>
              <span
                className={`ecg-semaforo-icon ${triState}`}
                style={{ display: 'inline-block', verticalAlign: 'middle', marginRight: 6 }}
              />
              {etiquetaImpactoTriState(triState)}
            </span>
          </div>
        </div>
        {triState === 'warning' && (
          <p className='ecg-hint' style={{ marginTop: '0.5rem', color: 'var(--vdr-warning)' }}>
            No se detecta empeoramiento relevante frente al caso base, pero el motor identifica refuerzos
            sugeridos en líneas o transformadores. Revise el detalle de refuerzos.
          </p>
        )}
        <p className='ecg-hint' style={{ marginTop: '0.25rem' }}>
          Indicador histórico de variación respecto al escenario sin proyecto. El veredicto normativo
          está en el bloque «Resumen de impacto» — este indicador no determina por sí solo la decisión.
        </p>
      </div>

      {mostrarDetalleEscenarios && !ip && (rt.estudiosDiariosBase.length === 0 || rt.estudiosSnapshotsBase.length === 0) && (
        <p className='ecg-hint ecg-hint-warn'>
          Este resultado no incluye análisis de impacto ni series base completas. Las tablas de detalle pueden mostrar
          sólo el caso con proyecto hasta que el motor las genere.
        </p>
      )}

      {mostrarDetalleEscenarios && (
      <div className='ecg-subsection' style={{ marginBottom: '1rem' }}>
        <p className='ecg-subsection-title'>Información adicional: estado de la red sin el proyecto</p>
        <p className='ecg-hint' style={{ marginTop: 0 }}>
          {casoBaseMalo === true
            ? 'En el escenario sin el proyecto, la red ya presenta incumplimientos según los criterios de la simulación (p. ej. proporción de elementos fuera de especificación). El impacto solo mide cuánto empeora o mejora el proyecto respecto a ese escenario de referencia.'
            : casoBaseMalo === false
              ? 'En los escenarios de referencia disponibles no se registran incumplimientos agregados.'
              : 'No se pudo determinar el estado de la red sin el proyecto (estudio antiguo).'}
        </p>
      </div>
      )}

      {mostrarDetalleEscenarios && restriccionesImpacto.length > 0 && (
        <div className='ecg-subsection' style={{ marginBottom: '1rem' }}>
          <p className='ecg-subsection-title'>
            Criterios donde el proyecto presenta impacto
          </p>
          <ul style={{ margin: 0, paddingLeft: '1.25rem' }}>
            {restriccionesImpacto.map((r) => (
              <li key={r}>{r}</li>
            ))}
          </ul>
        </div>
      )}

      {mostrarDetalleEscenarios && (
      <>
      <h4 className='vdr-result-h4'>
        Flujo diario — indicador de variación respecto al escenario sin proyecto
      </h4>
      <p className='ecg-hint' style={{ margin: '0 0 0.5rem' }}>
        Indicador histórico de variación (valor agregado, no decide la conexión). Verde: no se observa
        empeoramiento relevante frente al escenario sin proyecto; rojo: empeoramiento detectado por el motor.
        Gris: sin escenario de referencia disponible para comparar.
      </p>
      <table className='ecg-comparacion-table'>
        <thead>
          <tr>
            <th>Escenario</th>
            <th>Tensión</th>
            <th>Líneas</th>
            <th>Transformadores</th>
            <th>Pérdidas</th>
          </tr>
        </thead>
        <tbody>
          {rt.estudiosDiarios.map((r) => {
            const baseRow = mapDiarioBase.get(String(r.escenario ?? ''));
            const sm = semaforosDiarioDeltaVsBase(r, baseRow, umbral);
            return (
              <tr key={String(r.escenario)}>
                <td>{formatoEscenarioAmigable(String(r.escenario ?? ''))}</td>
                <td><SemaforoNullable ok={sm.tension} /></td>
                <td><SemaforoNullable ok={sm.corrientes} /></td>
                <td><SemaforoNullable ok={sm.trafos} /></td>
                <td><SemaforoNullable ok={sm.perdidas} /></td>
              </tr>
            );
          })}
          {rt.estudiosDiarios.length === 0 && (
            <tr>
              <td colSpan={5}><span className='ecg-hint'>Sin datos de estudios diarios.</span></td>
            </tr>
          )}
        </tbody>
      </table>

      <h4 className='vdr-result-h4'>
        Snapshots — indicador de variación respecto al escenario sin proyecto
      </h4>
      <p className='ecg-hint' style={{ margin: '0 0 0.5rem' }}>
        Referencia: snapshot sin generación del mismo periodo. Indicador histórico de variación; el veredicto
        normativo se encuentra en el bloque «Resumen de impacto» del estudio.
      </p>
      <table className='ecg-comparacion-table'>
        <thead>
          <tr>
            <th>Escenario</th>
            <th>Tensión</th>
            <th>Líneas</th>
            <th>Transformadores</th>
          </tr>
        </thead>
        <tbody>
          {rt.estudiosSnapshots
            .filter((r) => String(r.escenario ?? '').includes('genmax'))
            .map((r) => {
              const esc = String(r.escenario ?? '');
              const escB = escenarioSnapshotBase(esc);
              const baseRow = escB ? mapSnapBase.get(escB) : undefined;
              const sm = semaforosSnapshotDeltaVsBase(r, baseRow, umbral);
              return (
                <tr key={esc}>
                  <td>{etiquetaSnapshotCuatroCasos(esc)}</td>
                  <td><SemaforoNullable ok={sm.tension} /></td>
                  <td><SemaforoNullable ok={sm.corrientes} /></td>
                  <td><SemaforoNullable ok={sm.trafos} /></td>
                </tr>
              );
            })}
          {rt.estudiosSnapshots.filter((r) => String(r.escenario ?? '').includes('genmax')).length === 0 && (
            <tr>
              <td colSpan={4}><span className='ecg-hint'>Sin escenarios de generación máxima disponibles.</span></td>
            </tr>
          )}
        </tbody>
      </table>
      </>
      )}
    </div>
  );
};
export const BloqueTablaComparativaDiario: FunctionComponent<{
  data: SimpleObject;
  escenarioSeleccionado: string;
  onCambioEscenario: (v: string) => void;
}> = ({ data, escenarioSeleccionado, onCambioEscenario }) => {
  const rt = parseResultadoTecnico(data);
  const opts = escenariosDiariosConProyecto(rt);
  const con = rt.estudiosDiarios.find((r) => String(r.escenario) === escenarioSeleccionado);
  const mapBase = mapPorEscenario(rt.estudiosDiariosBase);
  const base = escenarioSeleccionado ? mapBase.get(escenarioSeleccionado) : undefined;
  const analisisEscenario = analisisDiarioPorEscenario(rt, escenarioSeleccionado);
  const perdidasArt = parsePerdidasArtefacto(data);
  const filas = construirFilasDiarioConVeredicto(con, base, analisisEscenario, perdidasArt);

  return (
    <div>
      <div className='ecg-form-row'>
        <div className='ecg-form-field' style={{ maxWidth: 420 }}>
          <label className='ecg-label' htmlFor='ecg-sel-diario'>Escenario de estudio diario</label>
          <select
            id='ecg-sel-diario'
            className='ecg-input'
            value={escenarioSeleccionado}
            onChange={(e) => onCambioEscenario(e.target.value)}>
            {opts.length === 0 && <option value=''>—</option>}
            {opts.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>
      </div>
      {!escenarioSeleccionado && <p className='ecg-hint'>Seleccione un escenario.</p>}
      {escenarioSeleccionado && (
        <>
        <p className='ecg-hint' style={{ marginTop: '0.75rem' }}>
          Valores reportados por la simulación (% respecto al escenario). «Sin empeoramiento» refleja el
          veredicto de la simulación para esa dimensión.
        </p>
          <div className='vdr-result-table-scroll'>
          <table className='ecg-comparacion-table ecg-tecnico-detalle-table' style={{ marginTop: '0.75rem' }}>
            <thead>
              <tr>
              <th>Criterio</th>
              <th className='vdr-result-num'>Sin proyecto</th>
              <th className='vdr-result-num'>Con proyecto</th>
              <th className='vdr-result-num'>Variación (pp)</th>
              <th>Sin empeoramiento</th>
            </tr>
          </thead>
          <tbody>
            {filas.map((f) => (
              <tr key={f.criterio}>
                <td>{f.criterio}</td>
                <td className='vdr-result-num'>{f.sinProyecto}</td>
                <td className='vdr-result-num'>{f.conProyecto}</td>
                <td className={`vdr-result-num ${celdaDeltaSignoClass(f.delta)}`}>{f.delta}</td>
                <td><CumpleCell v={f.cumple} /></td>
              </tr>
            ))}
          </tbody>
        </table>
          </div>
          <BloqueDeltaVPc analisis={analisisEscenario} contexto='diario' />
        </>
      )}
    </div>
  );
};

export const BloqueTablaComparativaSnapshot: FunctionComponent<{
  data: SimpleObject;
  escenarioSeleccionado: string;
  onCambioEscenario: (v: string) => void;
}> = ({ data, escenarioSeleccionado, onCambioEscenario }) => {
  const rt = parseResultadoTecnico(data);
  const opts = escenariosSnapshotConProyecto(rt);
  const con = rt.estudiosSnapshots.find((r) => String(r.escenario) === escenarioSeleccionado);
  const escBase = escenarioSeleccionado ? escenarioSnapshotBase(escenarioSeleccionado) : null;
  const mapBase = mapPorEscenario(rt.estudiosSnapshotsBase);
  const base = escBase ? mapBase.get(escBase) : undefined;
  const analisisEscenario = analisisSnapshotPorEscenario(rt, escenarioSeleccionado);
  const filas = construirFilasSnapshotConVeredicto(con, base, analisisEscenario);
  const congestionData = (data?.congestion_zonal ?? data?.congestionZonal) as
    | SimpleObject
    | undefined;

  return (
    <div>
      <div className='ecg-form-row'>
        <div className='ecg-form-field' style={{ maxWidth: 420 }}>
          <label className='ecg-label' htmlFor='ecg-sel-snap'>Escenario snapshot</label>
          <select
            id='ecg-sel-snap'
            className='ecg-input'
            value={escenarioSeleccionado}
            onChange={(e) => onCambioEscenario(e.target.value)}>
            {opts.length === 0 && <option value=''>—</option>}
            {opts.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>
      </div>
      {!escenarioSeleccionado && <p className='ecg-hint'>Seleccione un snapshot.</p>}
      {escenarioSeleccionado && (
        <>
          <p className='ecg-hint' style={{ marginTop: '0.75rem' }}>
            Pérdidas no aplican en snapshots (se evalúan en el estudio de pérdidas separado). Variación =
            (con proyecto − escenario sin proyecto emparejado). «Sin empeoramiento» refleja el veredicto
            de la simulación para esa dimensión.
          </p>
          <div className='vdr-result-table-scroll'>
          <table className='ecg-comparacion-table ecg-tecnico-detalle-table' style={{ marginTop: '0.75rem' }}>
            <thead>
              <tr>
              <th>Criterio</th>
              <th className='vdr-result-num'>Sin proyecto</th>
              <th className='vdr-result-num'>Con proyecto</th>
              <th className='vdr-result-num'>Variación (pp)</th>
              <th>Sin empeoramiento</th>
            </tr>
          </thead>
          <tbody>
            {filas.map((f) => (
              <tr key={f.criterio}>
                <td>{f.criterio}</td>
                <td className='vdr-result-num'>{f.sinProyecto}</td>
                <td className='vdr-result-num'>{f.conProyecto}</td>
                <td className={`vdr-result-num ${celdaDeltaSignoClass(f.delta)}`}>{f.delta}</td>
                <td><CumpleCell v={f.cumple} /></td>
              </tr>
            ))}
          </tbody>
        </table>
          </div>
          <BloqueDeltaVPc analisis={analisisEscenario} contexto='snapshot' />
          <BloqueCongestionTransmisionMulti
            escenarioSeleccionado={escenarioSeleccionado}
            filas={[{ titulo: 'Con proyecto', congestion: congestionData }]}
          />
        </>
      )}
    </div>
  );
};

/** Clase por signo: rojo si Δ > 0 (empeora), verde si Δ ≤ 0 (mejora o igual), neutro si —. */
function celdaDeltaSignoClass(delta: string): string {
  if (delta === '—') return '';
  const n = parseFloat(delta);
  if (!Number.isFinite(n)) return '';
  return n > 0 ? 'ecg-delta-warn' : 'ecg-delta-ok';
}

/** Veredicto del motor por criterio (!tiene_impacto ⇒ sin empeoramiento). */
function sinEmpeoramientoDesdeAnalisis(
  analisis: SimpleObject | undefined,
  criterio: string
): boolean | null {
  if (!analisis || typeof analisis !== 'object') return null;
  const sub = analisis[criterio] as SimpleObject | undefined;
  if (!sub || typeof sub !== 'object') return null;
  const v = sub.tiene_impacto ?? sub.tieneImpacto;
  if (typeof v === 'boolean') return !v;
  return null;
}

/**
 * NORM-018 A — construye filas de la tabla «Detalle flujo diario» con números
 * reales del motor (% pareo) y veredicto por criterio desde `impacto_proyecto`.
 *
 * Labels alineados con los de snapshots (son la misma métrica agregada de
 * cantidad de elementos con problema, no % de tiempo).
 *
 * Fila adicional «Pérdidas (% tiempo sobre umbral)»: indicador **propio de la
 * simulación diaria** (1440 min) que cuenta cuánto tiempo las pérdidas en la
 * red superan el umbral interno del motor. El veredicto se deriva localmente
 * comparando base vs con proyecto (sin empeoramiento ⇔ Δ ≤ 0): el estudio
 * normativo 24×96 tiene su propio espacio en la pestaña Detalle — pérdidas.
 */
function construirFilasDiarioConVeredicto(
  con: SimpleObject | undefined,
  base: SimpleObject | undefined,
  analisisEsc: SimpleObject | undefined,
  _perdidasArt: EcgPerdidasArtefacto | null
): FilaComparativa[] {
  if (!con) return [];
  const defs: { baseK: keyof SimpleObject; conK: keyof SimpleObject; label: string; critMotor: string }[] = [
    { baseK: 'pct_buses_con_problema', conK: 'pct_buses_con_problema', label: 'Tensión (% buses fuera de banda)', critMotor: 'tension' },
    { baseK: 'pct_lineas_con_problema', conK: 'pct_lineas_con_problema', label: 'Líneas — corrientes (% líneas en sobrecarga)', critMotor: 'corrientes' },
    { baseK: 'pct_trafos_con_problema', conK: 'pct_trafos_con_problema', label: 'Transformadores (% trafos en sobrecarga)', critMotor: 'trafos' }
  ];
  const filas: FilaComparativa[] = defs.map(({ baseK, conK, label, critMotor }) => {
    const b = base ? numO(base[baseK]) : null;
    const c = numO(con[conK]);
    const delta = b !== null && c !== null ? round2(c - b) : null;
    return {
      criterio: label,
      sinProyecto: fmtPct(b),
      conProyecto: fmtPct(c),
      delta: delta === null ? '—' : `${delta.toFixed(2)} pp`,
      cumple: sinEmpeoramientoDesdeAnalisis(analisisEsc, critMotor)
    };
  });

  // Pérdidas del flujo diario (indicador legacy del motor: % tiempo sobre umbral).
  const pBase = base ? numO(base.pct_tiempo_perdidas_sobre_umbral) : null;
  const pCon = numO(con.pct_tiempo_perdidas_sobre_umbral);
  if (pBase !== null || pCon !== null) {
    const delta = pBase !== null && pCon !== null ? round2(pCon - pBase) : null;
    const sinEmp = delta === null ? null : delta <= 0;
    filas.push({
      criterio: 'Pérdidas (% tiempo sobre umbral)',
      sinProyecto: fmtPct(pBase),
      conProyecto: fmtPct(pCon),
      delta: delta === null ? '—' : `${delta.toFixed(2)} pp`,
      cumple: sinEmp
    });
  }
  return filas;
}

/**
 * Filas «Detalle snapshots» con % del motor y veredicto por criterio (tensión,
 * líneas, trafos). No incluye pérdidas: en snapshots no aplica ese criterio.
 */
function construirFilasSnapshotConVeredicto(
  con: SimpleObject | undefined,
  base: SimpleObject | undefined,
  analisisEsc: SimpleObject | undefined
): FilaComparativa[] {
  if (!con) return [];
  const defs: { baseK: keyof SimpleObject; conK: keyof SimpleObject; label: string; critMotor: string }[] = [
    { baseK: 'pct_buses_fuera_banda', conK: 'pct_buses_fuera_banda', label: 'Tensión (% buses fuera de banda)', critMotor: 'tension' },
    { baseK: 'pct_lineas_sobrecarga', conK: 'pct_lineas_sobrecarga', label: 'Líneas — corrientes (% líneas en sobrecarga)', critMotor: 'corrientes' },
    { baseK: 'pct_trafos_sobrecarga', conK: 'pct_trafos_sobrecarga', label: 'Transformadores (% trafos en sobrecarga)', critMotor: 'trafos' }
  ];
  return defs.map(({ baseK, conK, label, critMotor }) => {
    const b = base ? numO(base[baseK]) : null;
    const c = numO(con[conK]);
    const delta = b !== null && c !== null ? round2(c - b) : null;
    return {
      criterio: label,
      sinProyecto: fmtPct(b),
      conProyecto: fmtPct(c),
      delta: delta === null ? '—' : `${delta.toFixed(2)} pp`,
      cumple: sinEmpeoramientoDesdeAnalisis(analisisEsc, critMotor)
    };
  });
}

// ═══════════════════════════════════════════════════════════════════════════
// NORM-018 A — ΔV en punto de conexión (NORM-013/014, §12.7, D3 Opción A)
// Se muestra bajo las tablas de snapshot y de flujo diario: es el **otro**
// criterio que, junto con conteo de elementos (N_base/N_proj), define el
// veredicto formal de impacto.
// ═══════════════════════════════════════════════════════════════════════════

export type EcgDeltaVPcDato = {
  vConPu: number | null;
  vSinPu: number | null;
  deltaVPct: number | null;
  limitePct: number | null;
  cumple: boolean | null;
  densidad: string | null;
  razon: string | null;
  tMax: number | null;
};

export function parseDeltaVPc(v: unknown): EcgDeltaVPcDato | null {
  const o = obj(v);
  if (!o) return null;
  return {
    vConPu: numO(o.v_con_pu ?? o.vConPu),
    vSinPu: numO(o.v_sin_pu ?? o.vSinPu),
    deltaVPct: numO(o.delta_v_pct ?? o.deltaVPct),
    limitePct: numO(o.limite_pct ?? o.limitePct),
    cumple: asBoolOrNull(o.cumple),
    densidad: asStringOrNull(o.densidad),
    razon: asStringOrNull(o.razon),
    tMax: numO(o.t_max ?? o.tMax)
  };
}

function fmtPu(n: number | null): string {
  if (n === null) return '—';
  return n.toFixed(4);
}

function fmtSignedPctShort(n: number | null): string {
  if (n === null) return '—';
  const r = Math.round(n * 100) / 100;
  const s = r > 0 ? '+' : '';
  return `${s}${r.toFixed(2)} %`;
}

function fmtMinutosDelDia(t: number | null): string | null {
  if (t === null || !Number.isFinite(t)) return null;
  const m = Math.max(0, Math.round(t)) % (24 * 60);
  const h = Math.floor(m / 60);
  const mm = m % 60;
  return `${String(h).padStart(2, '0')}:${String(mm).padStart(2, '0')}`;
}

const BloqueDeltaVPc: FunctionComponent<{
  analisis: SimpleObject | undefined;
  contexto: 'snapshot' | 'diario';
}> = ({ analisis, contexto }) => (
  <BloqueDeltaVPcMulti
    contexto={contexto}
    filas={[{ titulo: 'Con proyecto', analisis }]}
  />
);

/**
 * Bloque ΔV PC multifila: tabla con una fila por caso (proyecto y/o mitigaciones).
 * Ambas páginas (Fase 1 y Fase 2) lo usan: Fase 1 pasa una sola fila; Fase 2
 * pasa «Con proyecto (sin mitigación)» + una por medida visible.
 */
export const BloqueDeltaVPcMulti: FunctionComponent<{
  contexto: 'snapshot' | 'diario';
  filas: { titulo: string; analisis: SimpleObject | undefined }[];
}> = ({ contexto, filas }) => {
  const datos = filas.map((f) => ({
    titulo: f.titulo,
    dv: parseDeltaVPc(f.analisis?.delta_v_pc ?? f.analisis?.deltaVPc)
  }));

  if (datos.every((x) => x.dv === null)) {
    return (
      <p className='ecg-hint' style={{ marginTop: '0.75rem' }}>
        Este estudio no incluye información de <strong>variación de tensión en el punto de conexión (ΔV PC)</strong>{' '}
        para el escenario seleccionado.
      </p>
    );
  }

  return (
    <div className='ecg-subsection vdr-result-subblock'>
      <p className='ecg-subsection-title'>
        Variación de tensión en el punto de conexión (ΔV PC)
      </p>
      <p className='ecg-hint' style={{ margin: '0.25rem 0 0.5rem' }}>
        {contexto === 'snapshot'
          ? 'Diferencia de tensión en el punto de conexión con/sin el proyecto, con signo. Se evalúa la elevación de tensión contra el límite normativo según densidad del alimentador (Art. 3-19 NTCO).'
          : 'Elevación máxima de tensión en el punto de conexión durante el día (minuto del máximo ΔV PC entre la simulación con y sin el proyecto/medida, con signo). Se compara con el límite normativo según densidad del alimentador (Art. 3-19 NTCO).'}
      </p>
      <div className='vdr-result-table-scroll'>
      <table className='ecg-comparacion-table ecg-tecnico-detalle-table' style={{ marginTop: '0.25rem' }}>
        <thead>
          <tr>
            {datos.length > 1 && <th>Caso</th>}
            <th className='vdr-result-num'>V sin proyecto (p.u.)</th>
            <th className='vdr-result-num'>V con (p.u.)</th>
            <th className='vdr-result-num'>ΔV PC</th>
            <th className='vdr-result-num'>Límite</th>
            {contexto === 'diario' && <th className='vdr-result-num'>Minuto máx.</th>}
            <th>Cumple</th>
          </tr>
        </thead>
        <tbody>
          {datos.map(({ titulo, dv }, i) => {
            if (!dv) {
              return (
                <tr key={i}>
                  {datos.length > 1 && <td>{titulo}</td>}
                  <td colSpan={contexto === 'diario' ? 5 : 4}>
                    <span className='ecg-hint'>Sin información para este caso.</span>
                  </td>
                  <td><CumpleCell v={null} /></td>
                </tr>
              );
            }
            const sinDato = dv.razon === 'sin_dato' || dv.deltaVPct === null;
            const claseDelta = celdaDeltaSignoClass(fmtSignedPctShort(dv.deltaVPct));
            const horaMax = contexto === 'diario' ? fmtMinutosDelDia(dv.tMax) : null;
            return (
              <tr key={i}>
                {datos.length > 1 && <td>{titulo}</td>}
                <td className='vdr-result-num'>{fmtPu(dv.vSinPu)}</td>
                <td className='vdr-result-num'>{fmtPu(dv.vConPu)}</td>
                <td className={`vdr-result-num ${claseDelta}`}>{fmtSignedPctShort(dv.deltaVPct)}</td>
                <td className='vdr-result-num'>{dv.limitePct !== null ? `${dv.limitePct.toFixed(2)} %` : '—'}</td>
                {contexto === 'diario' && <td className='vdr-result-num'>{horaMax ?? '—'}</td>}
                <td><CumpleCell v={sinDato ? null : dv.cumple} /></td>
              </tr>
            );
          })}
        </tbody>
      </table>
      </div>
    </div>
  );
};

const CumpleCell: FunctionComponent<{ v: boolean | null }> = ({ v }) => {
  if (v === null) return <span className='ecg-hint'>N/D</span>;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap' }}>
      <span className={`ecg-semaforo-icon ${v ? 'cumple' : 'no-cumple'}`} style={{ display: 'inline-block' }} />
      {v ? 'Sí' : 'No'}
    </span>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// Congestión en transmisión (criterio zonal) — NORM-028 (etiqueta interna).
// Tabla por escenario seleccionado (solo escenarios genmax). Lee
// `congestion_zonal` desde el JSON de Fase 1 (con proyecto) y/o de cada
// mitigación (Fase 2). UI sin códigos de norma (DISENO_UX_*).
// ═══════════════════════════════════════════════════════════════════════════

export type EcgCongestionEscenario = {
  escenario: string;
  potenciaCabeceraKw: number | null;
  hayInversionCabecera: boolean;
  estado: string | null;
  cumple: boolean | null;
  flujoInversoZonalKw: number | null;
  iEquivalenteA: number | null;
  iLimTotalA: number | null;
  utilizacionPct: number | null;
};

export type EcgCongestionDato = {
  aplicable: boolean;
  estado: string | null;
  razon: string | null;
  cumple: boolean | null;
  iLimTotalA: number | null;
  demandaOtrosAlimKw: number | null;
  inyeccionOtrosKw: number | null;
  escenarios: EcgCongestionEscenario[];
};

function parseCongestionEscenario(v: unknown): EcgCongestionEscenario | null {
  const o = obj(v);
  if (!o) return null;
  const escenario = typeof o.escenario === 'string' ? o.escenario : '';
  return {
    escenario,
    potenciaCabeceraKw: numO(o.potencia_cabecera_kw ?? o.potenciaCabeceraKw),
    hayInversionCabecera:
      o.hay_inversion_cabecera === true || o.hayInversionCabecera === true,
    estado: asStringOrNull(o.estado),
    cumple: asBoolOrNull(o.cumple),
    flujoInversoZonalKw: numO(o.flujo_inverso_zonal_kw ?? o.flujoInversoZonalKw),
    iEquivalenteA: numO(o.i_equivalente_a ?? o.iEquivalenteA),
    iLimTotalA: numO(o.i_lim_total_a ?? o.iLimTotalA),
    utilizacionPct: numO(o.utilizacion_pct ?? o.utilizacionPct)
  };
}

export function parseCongestionTransmision(v: unknown): EcgCongestionDato | null {
  const o = obj(v);
  if (!o) return null;
  const cohorte = obj(o.cohorte) ?? {};
  const escenariosRaw = o.escenarios;
  const escenarios = Array.isArray(escenariosRaw)
    ? (escenariosRaw as unknown[])
        .map(parseCongestionEscenario)
        .filter((x): x is EcgCongestionEscenario => x !== null)
    : [];
  return {
    aplicable: o.aplicable !== false,
    estado: asStringOrNull(o.estado),
    razon: asStringOrNull(o.razon),
    cumple: asBoolOrNull(o.cumple),
    iLimTotalA: numO(o.i_lim_total_a ?? o.iLimTotalA),
    demandaOtrosAlimKw: numO(
      cohorte.demanda_otros_alimentadores_kw ?? cohorte.demandaOtrosAlimentadoresKw
    ),
    inyeccionOtrosKw: numO(
      cohorte.inyeccion_otros_alimentadores_kw ?? cohorte.inyeccionOtrosAlimentadoresKw
    ),
    escenarios
  };
}

/** Mensaje amigable a partir de los códigos `estado`/`razon` del motor. */
function mensajeCongestionNoEvaluable(cz: EcgCongestionDato): string {
  const r = cz.razon ?? '';
  if (r === 'sin_archivo_insumos_norm028') {
    return 'No se generaron los insumos de transmisión zonal para este estudio (no está el archivo de parámetros).';
  }
  if (r === 'archivo_insumos_norm028_invalido') {
    return 'Los insumos de transmisión zonal del estudio no se pudieron interpretar.';
  }
  if (r === 'sin_filas_transmision_para_subestacion') {
    return 'No hay datos de líneas de transmisión registradas para la subestación de este alimentador.';
  }
  if (r === 'insumos_no_disponibles') {
    return 'Los insumos para evaluar congestión en transmisión no están disponibles en este estudio.';
  }
  if (cz.estado === 'no_evaluable') {
    return 'El criterio de congestión en transmisión no es evaluable para este estudio.';
  }
  return 'Este estudio no incluye evaluación del criterio de congestión en transmisión.';
}

function fmtKw(n: number | null): string {
  if (n === null) return '—';
  const r = Math.round(n);
  const abs = Math.abs(r);
  const sign = r < 0 ? '−' : '';
  return `${sign}${abs.toLocaleString('es-CL')} kW`;
}

function fmtAmperes(n: number | null): string {
  if (n === null) return '—';
  return `${n.toLocaleString('es-CL', { maximumFractionDigits: 1 })} A`;
}

function fmtPctOneDecimal(n: number | null): string {
  if (n === null) return '—';
  return `${n.toFixed(1)} %`;
}

function flujoInversoZonalInferidoKw(esc: EcgCongestionEscenario, cz: EcgCongestionDato): number | null {
  if (esc.flujoInversoZonalKw !== null) return esc.flujoInversoZonalKw;
  if (esc.potenciaCabeceraKw === null || cz.inyeccionOtrosKw === null || cz.demandaOtrosAlimKw === null) return null;
  return Math.max(0, -esc.potenciaCabeceraKw + cz.inyeccionOtrosKw - cz.demandaOtrosAlimKw);
}

/** Cumple efectivo del criterio para el escenario:
 *  - si el escenario fue evaluado, lo que dice el motor;
 *  - si no hay flujo inverso en cabecera o el neto zonal no inyecta hacia
 *    transmisión, el criterio se cumple trivialmente;
 *  - en cualquier otro caso (sin datos, sin límite), null. */
function cumpleCriterioEscenario(esc: EcgCongestionEscenario, cz: EcgCongestionDato): boolean | null {
  if (esc.cumple === true || esc.cumple === false) return esc.cumple;
  if (!esc.hayInversionCabecera || esc.estado === 'no_evaluado_sin_inversion_cabecera') {
    return true;
  }
  if (esc.estado === 'no_evaluado_sin_inversion_zonal') return true;
  const flujoZonal = flujoInversoZonalInferidoKw(esc, cz);
  if (flujoZonal !== null && flujoZonal <= 0) return true;
  const iEq = esc.iEquivalenteA;
  const iLim = esc.iLimTotalA ?? cz.iLimTotalA;
  if (iEq !== null && iLim !== null) return iEq <= iLim;
  if (flujoZonal !== null && flujoZonal > 0 && iLim !== null && iLim <= 0) return false;
  return null;
}

/**
 * Bloque "Congestión en transmisión": muestra una tabla con una fila por caso
 * visible (Fase 1: solo "Con proyecto"; Fase 2: "Con proyecto (sin mitigación)"
 * + medidas seleccionadas). El selector de la pestaña ya limita los snapshots
 * relevantes; si el motor no trae una fila para el escenario, la tabla lo
 * informa sin ocultar el bloque.
 */
export const BloqueCongestionTransmisionMulti: FunctionComponent<{
  escenarioSeleccionado: string;
  filas: { titulo: string; congestion: SimpleObject | undefined | null }[];
}> = ({ escenarioSeleccionado, filas }) => {
  if (!escenarioSeleccionado) {
    return null;
  }

  const datos = filas.map((f) => ({
    titulo: f.titulo,
    cz: parseCongestionTransmision(f.congestion)
  }));

  const algunAplica = datos.some((x) => x.cz && x.cz.aplicable);
  if (!algunAplica) {
    // Diferenciamos: (a) el JSON del estudio no trae el bloque (motor previo
    // a la integración o pipeline antiguo), (b) el bloque viene con
    // `aplicable: false` (insumos no disponibles, etc.). El segundo caso
    // suele incluir `razon` por escenario para informar de manera concreta.
    const sinBloque = datos.every((x) => x.cz === null);
    const razonado = datos.find((x) => x.cz !== null)?.cz ?? null;
    return (
      <div className='ecg-subsection vdr-result-subblock'>
        <p className='ecg-subsection-title'>Congestión en transmisión</p>
        <p className='ecg-hint' style={{ margin: '0.25rem 0 0' }}>
          {sinBloque
            ? 'Este estudio fue ejecutado por una versión del motor que aún no incluye evaluación del criterio de congestión en transmisión, o no se generaron los insumos correspondientes.'
            : razonado
              ? mensajeCongestionNoEvaluable(razonado)
              : 'No fue posible evaluar el criterio de congestión en transmisión para este estudio.'}
        </p>
      </div>
    );
  }

  const masDeUna = datos.length > 1;
  const referencia = datos.find((x) => x.cz?.iLimTotalA != null)?.cz ?? null;
  const ilim = referencia?.iLimTotalA ?? null;

  return (
    <div className='ecg-subsection vdr-result-subblock'>
      <p className='ecg-subsection-title'>Congestión en transmisión</p>
      <p className='ecg-hint' style={{ margin: '0.25rem 0 0.5rem' }}>
        Evalúa si el flujo equivalente que la subestación inyecta a la red de transmisión supera la
        corriente máxima admisible. Solo aplica en escenarios de generación máxima y cuando hay flujo
        inverso en la cabecera del alimentador (la subestación pasa de consumir a inyectar).
      </p>
      {ilim !== null && (
        <p className='ecg-hint' style={{ margin: '0 0 0.5rem' }}>
          <strong>Corriente límite agregada:</strong> {fmtAmperes(ilim)}{' '}
          <span style={{ color: 'var(--vdr-mute)' }}>· suma de líneas de salida de la subestación</span>
        </p>
      )}
      <div className='vdr-result-table-scroll'>
      <table
        className='ecg-comparacion-table ecg-tecnico-detalle-table'
        style={{ marginTop: '0.25rem' }}>
        <thead>
          <tr>
            {masDeUna && <th>Caso</th>}
            <th
              className='vdr-result-num'
              title='Potencia activa neta del alimentador hacia la subestación (negativo: exporta a transmisión).'>
              Flujo en cabecera
            </th>
            <th
              className='vdr-result-num'
              title='Inyección agregada de generación distribuida en otros alimentadores de la misma subestación.'>
              Inyección otros PMGD
            </th>
            <th
              className='vdr-result-num'
              title='Demanda máxima agregada en otros alimentadores de la misma subestación.'>
              Demanda otros alimentadores
            </th>
            <th className='vdr-result-num'>Flujo inverso zonal</th>
            <th className='vdr-result-num'>Corriente equivalente</th>
            <th className='vdr-result-num'>Utilización</th>
            <th>Cumple</th>
          </tr>
        </thead>
        <tbody>
          {datos.map(({ titulo, cz }, i) => {
              if (!cz || !cz.aplicable) {
              return (
                <tr key={i}>
                  {masDeUna && <td>{titulo}</td>}
                  <td colSpan={6}>
                    <span className='ecg-hint'>No evaluado.</span>
                  </td>
                  <td><CumpleCell v={null} /></td>
                </tr>
              );
            }
            const escSel = cz.escenarios.find((e) => e.escenario === escenarioSeleccionado);
            if (!escSel) {
              return (
                <tr key={i}>
                  {masDeUna && <td>{titulo}</td>}
                  <td colSpan={6}>
                    <span className='ecg-hint'>Sin información para este caso.</span>
                  </td>
                  <td><CumpleCell v={null} /></td>
                </tr>
              );
            }
            const flujoCabTxt = (() => {
              const v = escSel.potenciaCabeceraKw;
              if (v === null) return '—';
              const txt = fmtKw(v);
              const tag = v < 0 ? ' (exportación)' : v > 0 ? ' (importación)' : '';
              return `${txt}${tag}`;
            })();
            const evaluado = escSel.hayInversionCabecera && escSel.estado === 'evaluado';
            const cumple = cumpleCriterioEscenario(escSel, cz);
            return (
              <tr key={i}>
                {masDeUna && <td>{titulo}</td>}
                <td className='vdr-result-num'>{flujoCabTxt}</td>
                <td className='vdr-result-num'>{fmtKw(cz.inyeccionOtrosKw)}</td>
                <td className='vdr-result-num'>{fmtKw(cz.demandaOtrosAlimKw)}</td>
                {evaluado ? (
                  <>
                    <td className='vdr-result-num'>{fmtKw(escSel.flujoInversoZonalKw)}</td>
                    <td className='vdr-result-num'>{fmtAmperes(escSel.iEquivalenteA)}</td>
                    <td className='vdr-result-num'>{fmtPctOneDecimal(escSel.utilizacionPct)}</td>
                  </>
                ) : (
                  <td colSpan={3} style={{ color: 'var(--vdr-mute)', textAlign: 'center' }}>
                    Sin flujo inverso
                  </td>
                )}
                <td><CumpleCell v={cumple} /></td>
              </tr>
            );
          })}
        </tbody>
      </table>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// NORM-018 A — §3.1.1 C + §3.1.3 V — Resumen de cumplimiento dual (formal/
// extendido) leído del contrato `resumen_cumplimiento` en la raíz del artefacto.
// Sustituye la narrativa del umbral 2 pp como verdad normativa.
// ═══════════════════════════════════════════════════════════════════════════

export type EcgVeredictoDetalle = {
  impactoSnapshotsConteo?: boolean | null;
  impactoSnapshotsDeltaV?: boolean | null;
  impactoPerdidas24d?: boolean | null;
  impactoDiariosConteo?: boolean | null;
  impactoDiariosDeltaV?: boolean | null;
  cortocircuitoPlaceholder?: string | null;
};

export type EcgResumenCumplimiento = {
  puedeConectarseSinMitigacion: boolean | null;
  proyectoTieneImpactoFormal: boolean | null;
  casoBaseIncumpleNormativa: boolean | null;
  notaOperacion: string | null;
  formalCumple: boolean | null;
  extendidoCumple: boolean | null;
  detalleFormal: EcgVeredictoDetalle;
  detalleExtendido: EcgVeredictoDetalle;
};

function asBoolOrNull(v: unknown): boolean | null {
  return typeof v === 'boolean' ? v : null;
}

function asStringOrNull(v: unknown): string | null {
  return typeof v === 'string' && v.trim() ? v : null;
}

function obj(v: unknown): SimpleObject | null {
  return v && typeof v === 'object' && !Array.isArray(v) ? (v as SimpleObject) : null;
}

/** Lee `resumen_cumplimiento` / `resumenCumplimiento` desde el payload base (raíz o wrapper camel). */
export function parseResumenCumplimiento(data: SimpleObject | null | undefined): EcgResumenCumplimiento | null {
  const d = obj(data);
  if (!d) return null;
  const rc = obj(d.resumen_cumplimiento) ?? obj(d.resumenCumplimiento);
  if (!rc) return null;

  const formal = obj(rc.normativa_formal) ?? obj(rc.normativaFormal);
  const ext = obj(rc.extendido_valor_agregado) ?? obj(rc.extendidoValorAgregado);
  const det = (v: SimpleObject | null): EcgVeredictoDetalle => {
    const inner = v ? (obj(v.detalle) ?? obj(v.detalle)) : null;
    return {
      impactoSnapshotsConteo: asBoolOrNull(inner?.impacto_por_conteo_snapshots ?? inner?.impactoPorConteoSnapshots),
      impactoSnapshotsDeltaV: asBoolOrNull(inner?.impacto_delta_v_pc_snapshots ?? inner?.impactoDeltaVPcSnapshots),
      impactoPerdidas24d: asBoolOrNull(inner?.impacto_perdidas_24d ?? inner?.impactoPerdidas24d),
      impactoDiariosConteo: asBoolOrNull(inner?.impacto_por_conteo_diarios ?? inner?.impactoPorConteoDiarios),
      impactoDiariosDeltaV: asBoolOrNull(inner?.impacto_delta_v_pc_diarios ?? inner?.impactoDeltaVPcDiarios),
      cortocircuitoPlaceholder: asStringOrNull(inner?.cortocircuito_placeholder ?? inner?.cortocircuitoPlaceholder)
    };
  };

  return {
    puedeConectarseSinMitigacion: asBoolOrNull(rc.puede_conectarse_sin_mitigacion ?? rc.puedeConectarseSinMitigacion),
    proyectoTieneImpactoFormal: asBoolOrNull(rc.proyecto_tiene_impacto_formal ?? rc.proyectoTieneImpactoFormal),
    casoBaseIncumpleNormativa: asBoolOrNull(rc.caso_base_incumple_normativa ?? rc.casoBaseIncumpleNormativa),
    notaOperacion: asStringOrNull(rc.nota_operacion ?? rc.notaOperacion),
    formalCumple: asBoolOrNull(formal?.cumple),
    extendidoCumple: asBoolOrNull(ext?.cumple),
    detalleFormal: det(formal),
    detalleExtendido: det(ext)
  };
}

export type EcgPerdidasAgregado = {
  fpPct: number | null;
  ePerdidasKwh: number | null;
  eRefKwh: number | null;
  nPasos: number | null;
};

export type EcgPerdidasArtefacto = {
  aplicable: boolean;
  razon: string | null;
  nota: string | null;
  fechas: string[];
  base: EcgPerdidasAgregado | null;
  con: EcgPerdidasAgregado | null;
  veredictoCumple: boolean | null;
  impactoTieneImpacto: boolean | null;
};

function parsePerdidasAgregado(v: unknown): EcgPerdidasAgregado | null {
  const o = obj(v);
  if (!o) return null;
  const fpPct = numO(o.fp_pct ?? o.fpPct);
  const fpRaw = numO(o.fp);
  const fallbackFpPct = fpPct ?? (fpRaw !== null ? fpRaw * 100 : null);
  return {
    fpPct: fallbackFpPct,
    ePerdidasKwh: numO(o.e_perdidas_kwh ?? o.ePerdidasKwh),
    eRefKwh: numO(o.e_ref_kwh ?? o.eRefKwh),
    nPasos: numO(o.n_pasos ?? o.nPasos)
  };
}

/** Lee `perdidas` de la raíz del artefacto base (§3.1.3 VI). */
export function parsePerdidasArtefacto(data: SimpleObject | null | undefined): EcgPerdidasArtefacto | null {
  const d = obj(data);
  if (!d) return null;
  const p = obj(d.perdidas);
  if (!p) return null;
  const sel = obj(p.seleccion_24_dias) ?? obj(p.seleccion24Dias);
  const fechasRaw = sel?.fechas;
  const fechas = Array.isArray(fechasRaw) ? (fechasRaw as unknown[]).map((x) => String(x)) : [];
  const veredicto = obj(p.veredicto);
  const impacto = obj(p.impacto);
  return {
    aplicable: p.aplicable === true,
    razon: asStringOrNull(p.razon),
    nota: asStringOrNull(p.nota),
    fechas,
    base: parsePerdidasAgregado(p.base),
    con: parsePerdidasAgregado(p.con),
    veredictoCumple: asBoolOrNull(veredicto?.cumple),
    impactoTieneImpacto: asBoolOrNull(impacto?.tiene_impacto ?? impacto?.tieneImpacto)
  };
}

/** Chip de estado (Sí/No/No aplica/No determinado) — presentación compacta. */
type EcgChipEstado = 'sin-impacto' | 'con-impacto' | 'no-aplica' | 'no-determinado';

function chipDesdeImpacto(tieneImpacto: boolean | null): EcgChipEstado {
  if (tieneImpacto === true) return 'con-impacto';
  if (tieneImpacto === false) return 'sin-impacto';
  return 'no-determinado';
}

function etiquetaChipEstado(e: EcgChipEstado): string {
  if (e === 'sin-impacto') return 'Sin impacto';
  if (e === 'con-impacto') return 'Con impacto';
  if (e === 'no-aplica') return 'No aplica';
  return '—';
}

function colorChipEstado(e: EcgChipEstado): string {
  if (e === 'sin-impacto') return 'cumple';
  if (e === 'con-impacto') return 'no-cumple';
  if (e === 'no-aplica') return 'warning';
  return 'nd';
}

const ChipDimension: FunctionComponent<{
  titulo: string;
  estado: EcgChipEstado;
  /** Texto opcional debajo del estado (p. ej. «0 equipos sobre 85 % ruptura»). */
  detalle?: string | null;
  /** Si se entrega, reemplaza la etiqueta por defecto de `estado`. */
  etiquetaEstadoOverride?: string;
  adicional?: boolean;
  tooltip?: string;
}> = ({ titulo, estado, detalle, etiquetaEstadoOverride, adicional, tooltip }) => (
  <div
    title={tooltip}
    className={adicional ? 'vdr-result-dim vdr-result-dim--adicional' : 'vdr-result-dim'}>
    <span className='vdr-result-dim-head'>
      <span className={`ecg-semaforo-icon ${colorChipEstado(estado)}`} style={{ display: 'inline-block' }} />
      <strong className='vdr-result-dim-title'>{titulo}</strong>
      {adicional && (
        <span
          className='vdr-result-tag'
          style={{
            fontSize: '10.5px', color: '#b45309', background: '#fff3e0', padding: '1px 6px'
          }}>Adicional</span>
      )}
    </span>
    <span className='vdr-result-dim-state'>{etiquetaEstadoOverride ?? etiquetaChipEstado(estado)}</span>
    {detalle && <span className='vdr-result-dim-detail'>{detalle}</span>}
  </div>
);

// ═══════════════════════════════════════════════════════════════════════════
// NORM-018 A — Cortocircuito: resumen corto (n_sobre_umbral, pct_sobre_umbral,
// cumple) leído de la raíz del artefacto base (`cortocircuito`).
// ═══════════════════════════════════════════════════════════════════════════

export type EcgCortocircuitoResumen = {
  cumple: boolean | null;
  nSobreUmbral: number | null;
  pctSobreUmbral: number | null;
};

export function parseCortocircuitoResumen(data: SimpleObject | null | undefined): EcgCortocircuitoResumen | null {
  const d = obj(data);
  if (!d) return null;
  const cc = obj(d.cortocircuito);
  if (!cc) return null;
  return {
    cumple: asBoolOrNull(cc.cumple),
    nSobreUmbral: numO(cc.n_sobre_umbral ?? cc.nSobreUmbral),
    pctSobreUmbral: numO(cc.pct_sobre_umbral ?? cc.pctSobreUmbral)
  };
}

/**
 * NORM-018 A §3.1.1 C — Bloque «Resumen de impacto» (reemplazo sin umbral 2 %).
 *
 * Lee el contrato `resumen_cumplimiento` + `perdidas` del artefacto base. Muestra:
 *  - titular «¿Puede conectarse sin mitigación?» (Sí / No / —)
 *  - banner «caso C» si viene `nota_operacion`
 *  - 4 chips: Instantáneos (snapshots), Pérdidas, Cortocircuito, Flujo horario (Adicional)
 *
 * Se usa en Fase 1 (pestaña 1 del bloque técnico) y en Fase 2 (§F.2, antes de la
 * tabla de cumplimiento/costos).
 */
export const BloqueVeredictoDual: FunctionComponent<{
  data: SimpleObject;
  /** Cuando es Fase 2, oculta la pregunta «puede conectarse sin mitigación» (aplica solo a base). */
  modoMitigacion?: boolean;
}> = ({ data, modoMitigacion = false }) => {
  const rc = parseResumenCumplimiento(data);
  const perdidas = parsePerdidasArtefacto(data);

  const snapshotsConImpacto = rc
    ? (rc.detalleFormal.impactoSnapshotsConteo === true || rc.detalleFormal.impactoSnapshotsDeltaV === true)
      ? true
      : (rc.detalleFormal.impactoSnapshotsConteo === false && rc.detalleFormal.impactoSnapshotsDeltaV === false)
        ? false
        : null
    : null;

  const diariosConImpacto = rc
    ? (rc.detalleExtendido.impactoDiariosConteo === true || rc.detalleExtendido.impactoDiariosDeltaV === true)
      ? true
      : (rc.detalleExtendido.impactoDiariosConteo === false && rc.detalleExtendido.impactoDiariosDeltaV === false)
        ? false
        : null
    : null;

  let chipPerdidas: EcgChipEstado;
  if (!perdidas || !perdidas.aplicable) {
    chipPerdidas = 'no-aplica';
  } else if (perdidas.impactoTieneImpacto !== null) {
    chipPerdidas = chipDesdeImpacto(perdidas.impactoTieneImpacto);
  } else if (rc?.detalleFormal.impactoPerdidas24d !== null && rc?.detalleFormal.impactoPerdidas24d !== undefined) {
    chipPerdidas = chipDesdeImpacto(rc.detalleFormal.impactoPerdidas24d);
  } else {
    chipPerdidas = 'no-determinado';
  }

  const chipSnapshots = chipDesdeImpacto(snapshotsConImpacto);
  const chipDiarios = chipDesdeImpacto(diariosConImpacto);

  // Cortocircuito: leemos el bloque real del artefacto (NORM-018 A). Si el
  // motor aún no entrega `cortocircuito` (estudio muy viejo), caemos al
  // placeholder histórico. La introducción de fallas adicionales (NORM-021)
  // será transparente para la UI: se suma al mismo agregado n_sobre_umbral.
  const ccResumen = parseCortocircuitoResumen(data);
  const ccPlaceholder = rc?.detalleFormal.cortocircuitoPlaceholder ?? null;

  let chipCC: EcgChipEstado;
  let etiquetaCC: string | undefined;
  let detalleCC: string | null = null;
  let tooltipCC: string | undefined;

  if (ccResumen && (ccResumen.cumple !== null || ccResumen.nSobreUmbral !== null)) {
    const n = ccResumen.nSobreUmbral;
    if (n === null) {
      chipCC = ccResumen.cumple === true ? 'sin-impacto' : ccResumen.cumple === false ? 'con-impacto' : 'no-determinado';
      etiquetaCC = undefined;
      detalleCC = null;
    } else if (n === 0) {
      chipCC = 'sin-impacto';
      etiquetaCC = 'Sin impacto';
      detalleCC = '0 equipos con Icc sobre el 85 % de capacidad de ruptura';
    } else {
      chipCC = 'con-impacto';
      etiquetaCC = 'Con impacto';
      detalleCC = `${n} equipo${n === 1 ? '' : 's'} con Icc sobre el 85 % de capacidad de ruptura`;
    }
    tooltipCC = 'Se cuenta cada equipo de protección cuya máxima corriente de cortocircuito simulada iguala o supera el 85 % de su capacidad de ruptura. Evaluación normativa actual: falla trifásica; la incorporación de otras fallas (p. ej. bifásica sin tierra) se sumará a este mismo indicador.';
  } else {
    chipCC = ccPlaceholder ? 'no-determinado' : 'sin-impacto';
    etiquetaCC = undefined;
    detalleCC = null;
    tooltipCC = ccPlaceholder
      ? 'El criterio de cortocircuito se reporta en modo transitorio hasta que se incorpore la evaluación normativa completa.'
      : undefined;
  }

  const puede = rc?.puedeConectarseSinMitigacion ?? null;
  const proyectoImpacto = rc?.proyectoTieneImpactoFormal ?? null;

  return (
    <div className='ecg-tecnico-resumen'>
      {!modoMitigacion && (
        <div className='ecg-subsection vdr-result-verdict'>
          <p className='ecg-subsection-title'>
            ¿Puede el proyecto conectarse sin mitigación?
          </p>
          <p className='vdr-result-verdict-answer'>
            <span
              className={`ecg-semaforo-icon ${
                puede === true ? 'cumple' : puede === false ? 'no-cumple' : 'nd'
              }`}
              style={{ display: 'inline-block' }}
            />
            {puede === true ? 'Sí' : puede === false ? 'No' : 'No determinado'}
            {proyectoImpacto !== null && (
              <span className='vdr-result-verdict-note'>
                {proyectoImpacto
                  ? '— el proyecto presenta impacto atribuible según criterios normativos del estudio.'
                  : '— no se detecta impacto atribuible al proyecto según criterios normativos del estudio.'}
              </span>
            )}
          </p>
          {rc?.notaOperacion && (
            <p className='vdr-result-nota-operativa'>
              <strong>Nota operativa:</strong> {rc.notaOperacion}
            </p>
          )}
        </div>
      )}

      <div className='vdr-result-dims'>
        <ChipDimension titulo='Snapshots' estado={chipSnapshots} />
        <ChipDimension titulo='Pérdidas energéticas' estado={chipPerdidas}
          tooltip={chipPerdidas === 'no-aplica'
            ? 'El estudio normativo de pérdidas no se ejecuta para este tipo de proyecto.'
            : undefined} />
        <ChipDimension titulo='Cortocircuito' estado={chipCC} detalle={detalleCC}
          etiquetaEstadoOverride={etiquetaCC} tooltip={tooltipCC} />
        <ChipDimension titulo='Flujo diario' estado={chipDiarios} adicional
          tooltip='El flujo diario es información adicional; no determina por sí solo la decisión de conexión.' />
      </div>

      <p className='ecg-hint' style={{ margin: 0 }}>
        La decisión considera snapshots, pérdidas (si aplican) y cortocircuito; el flujo diario es información adicional.
      </p>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// NORM-018 A — §3.1.1 E — «Detalle pérdidas» Fase 1 (minimalista).
// Muestra solo lo que viene en `perdidas` del artefacto base: comparativo
// agregado base vs con proyecto + 24 fechas colapsable + criterio normativo.
// ═══════════════════════════════════════════════════════════════════════════

function fmtKwh(n: number | null): string {
  if (n === null) return '—';
  if (Math.abs(n) >= 1e6) return `${(n / 1e6).toFixed(2)} GWh`;
  if (Math.abs(n) >= 1e3) return `${(n / 1e3).toFixed(2)} MWh`;
  return `${n.toFixed(0)} kWh`;
}

function fmtFp(n: number | null): string {
  if (n === null) return '—';
  return `${n.toFixed(2)}%`;
}

function fmtSignedPct(n: number | null): string {
  if (n === null || !Number.isFinite(n)) return '—';
  const rounded = Math.round(n * 100) / 100;
  const signo = rounded > 0 ? '+' : '';
  return `${signo}${rounded.toFixed(2)}%`;
}

function fmtSignedKwh(n: number | null): string {
  if (n === null || !Number.isFinite(n)) return '—';
  const abs = Math.abs(n);
  const base =
    abs >= 1e6 ? `${(n / 1e6).toFixed(2)} GWh`
      : abs >= 1e3 ? `${(n / 1e3).toFixed(2)} MWh`
        : `${n.toFixed(0)} kWh`;
  return n > 0 ? `+${base}` : base;
}

/** Δ% relativo de FP con signo: (fp_con − fp_base) / fp_base · 100. */
function variacionFpSignadaPct(base: EcgPerdidasAgregado | null, con: EcgPerdidasAgregado | null): number | null {
  const b = base?.fpPct ?? null;
  const c = con?.fpPct ?? null;
  if (b === null || c === null || !Number.isFinite(b) || !Number.isFinite(c) || Math.abs(b) < 1e-6) return null;
  return ((c - b) / b) * 100;
}

const PerdidasComparativoTabla: FunctionComponent<{
  base: EcgPerdidasAgregado | null;
  comparaciones: { titulo: string; datos: EcgPerdidasAgregado | null; destacado?: boolean }[];
}> = ({ base, comparaciones }) => {
  const cols = [{ titulo: 'Red sin proyecto', datos: base }, ...comparaciones];
  return (
    <div className='vdr-result-table-scroll'>
    <table className='ecg-comparacion-table ecg-tecnico-detalle-table'>
      <thead>
        <tr>
          <th>Indicador</th>
          {cols.map((c) => (
            <th
              key={c.titulo}
              className='vdr-result-num'
              style={(c as { destacado?: boolean }).destacado ? { background: 'var(--vdr-cyan-tint)', color: 'var(--vdr-ink)' } : undefined}>
              {c.titulo}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>Factor de pérdidas (FP)</td>
          {cols.map((c) => <td key={c.titulo} className='vdr-result-num'>{fmtFp(c.datos?.fpPct ?? null)}</td>)}
        </tr>
        <tr>
          <td>Variación FP respecto al caso base (Δ%)</td>
          <td className='vdr-result-num' style={{ color: 'var(--vdr-mute-2)' }}>—</td>
          {comparaciones.map((c) => {
            const dv = variacionFpSignadaPct(base, c.datos);
            return (
              <td key={c.titulo} className={`vdr-result-num ${celdaDeltaSignoClass(fmtSignedPct(dv))}`}>{fmtSignedPct(dv)}</td>
            );
          })}
        </tr>
        <tr>
          <td>Energía de pérdidas</td>
          {cols.map((c) => <td key={c.titulo} className='vdr-result-num'>{fmtKwh(c.datos?.ePerdidasKwh ?? null)}</td>)}
        </tr>
        <tr>
          <td>Variación energía de pérdidas (ΔE)</td>
          <td className='vdr-result-num' style={{ color: 'var(--vdr-mute-2)' }}>—</td>
          {comparaciones.map((c) => {
            const de = base?.ePerdidasKwh != null && c.datos?.ePerdidasKwh != null
              ? (c.datos.ePerdidasKwh - base.ePerdidasKwh) : null;
            return (
              <td key={c.titulo} className={`vdr-result-num ${celdaDeltaSignoClass(fmtSignedKwh(de))}`}>{fmtSignedKwh(de)}</td>
            );
          })}
        </tr>
        <tr>
          <td>Energía de referencia</td>
          {cols.map((c) => <td key={c.titulo} className='vdr-result-num'>{fmtKwh(c.datos?.eRefKwh ?? null)}</td>)}
        </tr>
      </tbody>
    </table>
    </div>
  );
};

const AcordeonFechas24: FunctionComponent<{ fechas: string[] }> = ({ fechas }) => {
  if (!fechas.length) return null;
  return (
    <details className='vdr-result-fechas'>
      <summary>
        {fechas.length} días representativos usados para el estudio de pérdidas
      </summary>
      <p className='ecg-hint' style={{ margin: '0.4rem 0 0.5rem' }}>
        Días elegidos para representar el comportamiento anual de demanda / generación.
      </p>
      <div className='vdr-result-fechas-list'>
        {fechas.map((f) => (
          <span key={f} className='vdr-result-fecha'>{f}</span>
        ))}
      </div>
    </details>
  );
};

export const BloqueDetallePerdidas: FunctionComponent<{
  data: SimpleObject;
}> = ({ data }) => {
  const p = parsePerdidasArtefacto(data);
  if (!p) {
    return (
      <p className='ecg-hint'>
        Esta versión del estudio no incluye datos de pérdidas energéticas.
      </p>
    );
  }

  if (!p.aplicable) {
    return (
      <div className='ecg-subsection vdr-result-callout-warn'>
        <p className='ecg-subsection-title' style={{ margin: 0 }}>Pérdidas energéticas — no aplica</p>
        <p className='vdr-result-callout-text'>
          Para este tipo de proyecto el estudio normativo de pérdidas previsto en PMGD (Art. 3-26) no se
          ejecuta. No hay tabla comparativa que reportar en esta pestaña.
        </p>
      </div>
    );
  }

  const tieneImpacto =
    p.impactoTieneImpacto !== null
      ? p.impactoTieneImpacto
      : p.veredictoCumple !== null
        ? !p.veredictoCumple
        : null;

  const estado: EcgChipEstado = chipDesdeImpacto(tieneImpacto);

  return (
    <div>
      <div
        className='ecg-subsection vdr-result-question'
        title='Atribución de impacto del proyecto (ΔFP con signo vs FP_base y ΔE vs 10% EMDA, Art. 3-26). Si el proyecto reduce pérdidas, no se atribuye impacto.'>
        <p className='ecg-subsection-title' style={{ margin: 0 }}>
          ¿Hay impacto en pérdidas atribuible al proyecto?
        </p>
        <p className='vdr-result-question-answer'>
          <span className={`ecg-semaforo-icon ${colorChipEstado(estado)}`}
            style={{ display: 'inline-block' }} />
          {tieneImpacto === true ? 'Sí' : tieneImpacto === false ? 'No' : 'No determinado'}
        </p>
      </div>

      <PerdidasComparativoTabla
        base={p.base}
        comparaciones={[{ titulo: 'Red con proyecto', datos: p.con }]}
      />

      <AcordeonFechas24 fechas={p.fechas} />
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// NORM-018 A — §3.2.1 F.6 — «Detalle pérdidas» en Fase 2 (comparativo 3 vías).
// Lee: base desde artefacto base; «con proyecto sin medida» desde `perdidas.con`
// del artefacto base; «con medida M» desde `comparacion.mitigaciones[i].perdidas`.
// ═══════════════════════════════════════════════════════════════════════════

export const BloqueDetallePerdidasMitigacion: FunctionComponent<{
  resultadoTecnicoData: SimpleObject | undefined;
  comparacion: SimpleObject | null;
  mitigaciones: SimpleObject[];
  selectedMitId: string;
  onChangeMitigacion: (v: string) => void;
}> = ({ resultadoTecnicoData, comparacion, mitigaciones, selectedMitId, onChangeMitigacion }) => {
  const pBase = resultadoTecnicoData ? parsePerdidasArtefacto(resultadoTecnicoData) : null;

  if (!pBase) {
    return (
      <p className='ecg-hint'>
        El estudio base no entregó datos de pérdidas normativas. Re-ejecute el estudio para ver este detalle.
      </p>
    );
  }

  if (!pBase.aplicable) {
    return (
      <div className='ecg-subsection vdr-result-callout-warn'>
        <p className='ecg-subsection-title' style={{ margin: 0 }}>Pérdidas energéticas — no aplica</p>
        <p className='vdr-result-callout-text'>
          Para este tipo de proyecto no se ejecuta el estudio de pérdidas normativo previsto en PMGD
          (Art. 3-26). No hay datos comparativos por medida.
        </p>
      </div>
    );
  }

  const mitItems: SimpleObject[] = Array.isArray(comparacion?.mitigaciones) ? (comparacion!.mitigaciones as SimpleObject[]) : [];

  // Emparejar cada mitItem (orden lista motor) con el registro BD (para id / orden visual).
  // Veredicto = impacto atribuible con signo (D-BP-4) — mismo criterio que Fase 1.
  const opciones = mitigaciones.map((m, idx) => {
    const mItem = mitItems[idx];
    return {
      id: String(m.id ?? idx),
      label: `Medida ${m.orden != null ? Number(m.orden) + 1 : idx + 1} — ${String(m.tipoMitigacion ?? m.tipo_mitigacion ?? mItem?.tipo ?? '').replace(/_/g, ' ')}`,
      perdidas: mItem ? parsePerdidasAgregadoFromComparacion(mItem.perdidas) : null,
      sinImpacto: perdidasCumpleMitigacion(mItem)
    };
  });

  const seleccionada = opciones.find((o) => o.id === selectedMitId) ?? opciones[0];
  const sinImpacto = seleccionada?.sinImpacto ?? null;
  const tieneImpacto = sinImpacto === null ? null : !sinImpacto;
  const estado: EcgChipEstado = chipDesdeImpacto(tieneImpacto);

  return (
    <div>
      {opciones.length > 1 && (
        <div className='ecg-form-row' style={{ marginBottom: '0.75rem' }}>
          <div className='ecg-form-field' style={{ maxWidth: 420 }}>
            <label className='ecg-label' htmlFor='ecg-sel-perd-med'>Medida de mitigación</label>
            <select
              id='ecg-sel-perd-med'
              className='ecg-input'
              value={seleccionada?.id ?? ''}
              onChange={(e) => onChangeMitigacion(e.target.value)}>
              {opciones.map((o) => (
                <option key={o.id} value={o.id}>{o.label}</option>
              ))}
            </select>
          </div>
        </div>
      )}

      <div
        className='ecg-subsection vdr-result-question'
        title='Atribución de impacto de la medida (ΔFP con signo vs FP_base y ΔE vs 10 % EMDA, Art. 3-26). Si la medida reduce pérdidas, no se atribuye impacto.'>
        <p className='ecg-subsection-title' style={{ margin: 0 }}>
          ¿Hay impacto en pérdidas atribuible a la medida? — {seleccionada?.label ?? 'medida'}
        </p>
        <p className='vdr-result-question-answer'>
          <span className={`ecg-semaforo-icon ${colorChipEstado(estado)}`}
            style={{ display: 'inline-block' }} />
          {tieneImpacto === true ? 'Sí' : tieneImpacto === false ? 'No' : 'No determinado'}
        </p>
      </div>

      <PerdidasComparativoTabla
        base={pBase.base}
        comparaciones={[
          { titulo: 'Con proyecto (sin esta medida)', datos: pBase.con },
          { titulo: 'Con medida seleccionada', datos: seleccionada?.perdidas ?? null, destacado: true }
        ]}
      />

      {!seleccionada?.perdidas && (
        <p className='ecg-hint' style={{ marginTop: '0.5rem' }}>
          La medida seleccionada no tiene pérdidas 24×96 registradas; la comparación se limita al caso con
          proyecto del estudio base.
        </p>
      )}

      <AcordeonFechas24 fechas={pBase.fechas} />
    </div>
  );
};

function parsePerdidasAgregadoFromComparacion(v: unknown): EcgPerdidasAgregado | null {
  const o = obj(v);
  if (!o) return null;
  const fpPct = numO(o.fp_pct ?? o.fpPct);
  const fpRaw = numO(o.fp);
  return {
    fpPct: fpPct ?? (fpRaw !== null ? fpRaw * 100 : null),
    ePerdidasKwh: numO(o.e_perdidas_kwh ?? o.ePerdidasKwh),
    eRefKwh: numO(o.e_ref_kwh ?? o.eRefKwh),
    nPasos: numO(o.n_pasos ?? o.nPasos)
  };
}

/**
 * Veredicto de impacto en pérdidas de una mitigación — **alineado con Fase 1**
 * (D-BP-4, §12.5 / NORM-023): se atribuye impacto **solo** si las pérdidas
 * aumentan más del umbral (ΔFP con signo). Si la medida **reduce** las
 * pérdidas, no hay impacto atribuible aunque `|ΔFP|` supere el umbral.
 *
 * Fuente preferida: `mitItem.veredicto_formal.detalle.perdidas_impacto.tiene_impacto`
 * (`evaluar_impacto_perdidas` base vs medida). Fallback a `restricciones_pendientes`
 * del motor (misma categoría «perdidas» agregada por NORM-026). En último caso,
 * estudios legacy sin esos campos caen al veredicto formal `|·|`
 * (`perdidas.veredicto_vs_base.cumple`) como mejor aproximación disponible.
 *
 * Devuelve `true` si no hay impacto atribuible, `false` si sí lo hay, `null`
 * si no aplica (EG) o no se pudo determinar.
 */
export function perdidasCumpleMitigacion(mitItem: SimpleObject | undefined): boolean | null {
  if (!mitItem) return null;
  const perd = obj(mitItem.perdidas);
  if (perd && perd.aplicable === false) return null;

  // 1) Preferente: veredicto con signo (D-BP-4).
  const vf = obj(mitItem.veredicto_formal) ?? obj(mitItem.veredictoFormal);
  const detalleF = obj(vf?.detalle);
  const perdImpacto = obj(detalleF?.perdidas_impacto) ?? obj(detalleF?.perdidasImpacto);
  const tieneImpacto = perdImpacto
    ? asBoolOrNull(perdImpacto.tiene_impacto ?? perdImpacto.tieneImpacto)
    : null;
  if (tieneImpacto !== null) return !tieneImpacto;

  // 2) Fallback: categoría "perdidas" en restricciones_pendientes (misma señal
  //    que el motor integra desde el análisis con signo en NORM-026).
  const pendientesRaw = (mitItem.restricciones_pendientes ?? mitItem.restriccionesPendientes) as unknown;
  if (Array.isArray(pendientesRaw)) {
    const pendientes = new Set((pendientesRaw as unknown[]).map((x) => String(x)));
    return !pendientes.has('perdidas');
  }

  // 3) Último recurso (estudios legacy): veredicto absoluto Art. 3-26 literal.
  const veredicto = obj(perd?.veredicto_vs_base) ?? obj(perd?.veredictoVsBase);
  return asBoolOrNull(veredicto?.cumple);
}

/**
 * Resumen de cumplimiento de pérdidas 24×96 para «Con proyecto (sin mitigación)»
 * leído desde la raíz del artefacto base. Devuelve `null` cuando no aplica o no
 * hay veredicto explícito.
 *
 * **Alineado con Fase 1 (D-BP-4, §12.5 / NORM-023):** prioriza el criterio con
 * **signo** (`perdidas.impacto.tiene_impacto`) — si el proyecto reduce pérdidas
 * no se atribuye impacto, aun cuando `|ΔFP|` supere el umbral. El veredicto
 * formal literal `|·|` (`perdidas.veredicto.cumple`) queda como respaldo para
 * estudios legacy que no traigan el bloque de impacto con signo.
 */
export function perdidasCumpleProyecto(data: SimpleObject | undefined): boolean | null {
  if (!data) return null;
  const p = parsePerdidasArtefacto(data);
  if (!p) return null;
  if (!p.aplicable) return null;
  if (p.impactoTieneImpacto !== null) return !p.impactoTieneImpacto;
  if (p.veredictoCumple !== null) return p.veredictoCumple;
  return null;
}

// ═══════════════════════════════════════════════════════════════════════════
// NORM-018 A — badge visual «Información adicional» para tabs/filas de diarios.
// ═══════════════════════════════════════════════════════════════════════════

export const BadgeAdicional: FunctionComponent<{ small?: boolean }> = ({ small }) => (
  <span
    className='vdr-result-tag'
    style={{
      fontSize: small ? '10.5px' : '11px',
      color: '#b45309',
      background: '#fff3e0',
      padding: small ? '1px 5px' : '2px 7px',
      marginLeft: 8
    }}>Adicional</span>
);
